import React, { useState, useRef, useEffect } from 'react';
import { PipelineStage, ExpertiseData } from '../../types/pipeline.types';
import { getSpecialCounts } from '../../utils/pipelineHelpers';
import './StagesGrid.styles.css';

interface StagesGridProps {
  stages: PipelineStage[];
  counts: Record<number, number>;
  urgentCounts?: Record<number, number>;
  expertises: ExpertiseData[];
  selectedStageId: number | null;
  onStageSelect: (stageId: number) => void;
}

export const StagesGrid: React.FC<StagesGridProps> = ({
  stages,
  counts,
  urgentCounts,
  expertises,
  selectedStageId,
  onStageSelect
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const stagesGridRef = useRef<HTMLDivElement>(null);

  const handleStageClick = (stageId: number) => {
    if (counts[stageId] > 0) {
      onStageSelect(stageId);
    }
  };

  const checkScrollability = () => {
    if (stagesGridRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = stagesGridRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollStages = (direction: 'left' | 'right') => {
    if (stagesGridRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = direction === 'left' 
        ? stagesGridRef.current.scrollLeft - scrollAmount
        : stagesGridRef.current.scrollLeft + scrollAmount;
      
      stagesGridRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollability();
    
    const handleResize = () => checkScrollability();
    window.addEventListener('resize', handleResize);
    
    const stagesGrid = stagesGridRef.current;
    if (stagesGrid) {
      stagesGrid.addEventListener('scroll', checkScrollability);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (stagesGrid) {
        stagesGrid.removeEventListener('scroll', checkScrollability);
      }
    };
  }, [stages]);

  return (
    <div className="stages-container">
      {/* Left scroll arrow */}
      <button 
        className={`scroll-arrow scroll-arrow-left ${!canScrollLeft ? 'disabled' : ''}`}
        onClick={() => scrollStages('left')}
        disabled={!canScrollLeft}
        aria-label="Scroll left"
      >
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {/* Stages grid */}
      <div 
        className="stages-grid" 
        ref={stagesGridRef}
        onScroll={checkScrollability}
      >
        {stages.map((stage) => {
          const count = counts[stage.id] || 0;
          const specialCount = urgentCounts ? urgentCounts[stage.id] || 0 : getSpecialCounts(expertises, stage.id);
          const maxCount = 200; // Maximum for full circle
          const percentage = Math.min((count / maxCount) * 100, 100);
          const circumference = 2 * Math.PI * 26; // radius of 26px
          const strokeDashoffset = circumference - (percentage / 100) * circumference;
          
          return (
            <div
              key={stage.id}
              className={`stage-card ${count === 0 ? 'empty' : ''} ${selectedStageId === stage.id ? 'selected' : ''}`}
              onClick={() => handleStageClick(stage.id)}
            >
              <div 
                className="stage-icon-container"
                style={{ backgroundColor: stage.color }}
              >
                {/* Circular progress gauge */}
                {count > 0 && (
                  <svg className="stage-progress-ring" width="68" height="68" viewBox="0 0 68 68">
                    {/* Background circle */}
                    <circle
                      className="progress-ring-background"
                      cx="34"
                      cy="34"
                      r="26"
                      strokeWidth="3"
                      fill="transparent"
                    />
                    {/* Progress circle */}
                    <circle
                      className="progress-ring-progress"
                      cx="34"
                      cy="34"
                      r="26"
                      strokeWidth="3"
                      fill="transparent"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                    />
                  </svg>
                )}
                
                <span className="stage-icon">
                  {stage.icon}
                </span>
                
                {/* Count badges */}
                {count > 0 && (
                  <>
                    {/* Total count badge (green) */}
                    <div className="stage-count-badge total-count">
                      <span className="count-number">{count}</span>
                    </div>
                    {/* Special count badge (urgent) */}
                    {specialCount > 0 && (
                      <div className="stage-count-badge special-count">
                        <span className="count-number">{specialCount}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <h3 className="stage-title">{stage.label}</h3>
            </div>
          );
        })}
      </div>
      
      {/* Right scroll arrow */}
      <button 
        className={`scroll-arrow scroll-arrow-right ${!canScrollRight ? 'disabled' : ''}`}
        onClick={() => scrollStages('right')}
        disabled={!canScrollRight}
        aria-label="Scroll right"
      >
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};